import { MAX_CLIENT_RPC_FRAME_BYTES } from "../client-rpc/frame.js";
/**
 * Bounded DataChannel binary-message segmentation for the WebRTC carrier
 * (ticket #1825, "N4"). Mirrors
 * `core/adapters-network/src/webrtc/segmentation.rs` byte-for-byte (same
 * 1-byte header, same chunk size) so the browser and Rust carriers speak
 * one chunk wire format.
 *
 * A `node/client-rpc/1` frame (already length-prefixed by
 * `encodeClientRpcFrame`) can be larger than the DataChannel's practical
 * per-message limit, so this module splits an already-encoded client-rpc
 * frame into a bounded sequence of DataChannel messages and reassembles
 * them back into one buffer, capped at `MAX_CLIENT_RPC_FRAME_BYTES`.
 *
 * Wire format per DataChannel message: one flag byte followed by the chunk
 * payload. Bit 0 of the flag byte is `MORE` (`1` = more chunks follow this
 * one and belong to the same reassembled frame, `0` = this chunk completes
 * the frame).
 */
/**
 * Conservative, cross-implementation-safe DataChannel message size. Some
 * SCTP/DataChannel stacks (notably older Safari) cap unnegotiated message
 * sizes at 16 KiB even when the negotiated SDP `a=max-message-size` allows
 * more; chunking this small keeps every carrier interoperable without
 * first having to parse and trust the remote's advertised maximum.
 */
export const MAX_DATACHANNEL_CHUNK_BYTES = 16 * 1024;
const FLAG_MORE = 0b0000_0001;
const HEADER_BYTES = 1;
export class DataChannelSegmentationError extends Error {
    constructor(message) {
        super(message);
        this.name = "DataChannelSegmentationError";
    }
}
/**
 * Split one already-encoded client-rpc frame into a bounded sequence of
 * DataChannel messages, each at most `chunkSize` bytes including the
 * 1-byte header. Never assumes the frame fits in a single `send()`.
 */
export function segmentFrame(frame, chunkSize = MAX_DATACHANNEL_CHUNK_BYTES) {
    const boundedChunkSize = Math.max(chunkSize, HEADER_BYTES + 1);
    const payloadSize = boundedChunkSize - HEADER_BYTES;
    if (frame.byteLength === 0)
        return [Uint8Array.of(0)];
    const chunks = [];
    let offset = 0;
    while (offset < frame.byteLength) {
        const end = Math.min(offset + payloadSize, frame.byteLength);
        const more = end < frame.byteLength;
        const chunk = new Uint8Array(1 + (end - offset));
        chunk[0] = more ? FLAG_MORE : 0;
        chunk.set(frame.subarray(offset, end), 1);
        chunks.push(chunk);
        offset = end;
    }
    return chunks;
}
/**
 * Bounded reassembler for one logical client-rpc frame at a time. One
 * DataChannel carries one request/response (or a cancel) in flight per
 * direction at a time, so one reassembler instance per direction is
 * sufficient.
 *
 * Accumulates chunk payloads in a list and concatenates once, on
 * completion, rather than reallocating/copying the whole buffer on every
 * chunk — the latter is quadratic in the number of chunks for a large
 * frame (hundreds of chunks at the shared client-rpc max).
 */
export class DataChannelFrameReassembler {
    #chunks = [];
    #length = 0;
    /**
     * Feed one DataChannel message. Returns the reassembled frame once the
     * final chunk (`MORE` unset) completes it, or `undefined` if more chunks
     * are still expected. The reassembled size is bounded at
     * `MAX_CLIENT_RPC_FRAME_BYTES`; an oversized in-progress frame is
     * dropped (not just truncated) so a misbehaving/attacking peer cannot
     * use partial state to bypass the bound on a retry.
     */
    push(chunk) {
        if (chunk.byteLength === 0)
            throw new DataChannelSegmentationError("datachannel chunk is empty");
        const flag = chunk[0];
        const payload = chunk.subarray(1);
        if (this.#length + payload.byteLength > MAX_CLIENT_RPC_FRAME_BYTES) {
            this.reset();
            throw new DataChannelSegmentationError("reassembled datachannel frame exceeds the maximum client-rpc frame size");
        }
        this.#chunks.push(payload);
        this.#length += payload.byteLength;
        if ((flag & FLAG_MORE) === FLAG_MORE)
            return undefined;
        const frame = new Uint8Array(this.#length);
        let offset = 0;
        for (const part of this.#chunks) {
            frame.set(part, offset);
            offset += part.byteLength;
        }
        this.reset();
        return frame;
    }
    /** Discard any partially reassembled frame. */
    reset() {
        this.#chunks = [];
        this.#length = 0;
    }
}
//# sourceMappingURL=datachannel-segmentation.js.map